import React, { useContext, useState, FormEvent } from 'react';
import styled from 'styled-components';
import { ContactContext } from './ContactContext';
import mediaQueries from '../../styles/mediaQueries';

// Types pour le formulaire
interface FormData {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  company: string;
  message: string;
}

// État initial du formulaire
const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  company: '',
  message: ''
};

// Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  padding: 30px;
  position: relative;
  
  ${mediaQueries.small} {
    padding: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.darkGray};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.secondary};
  text-align: center;
  font-weight: var(--font-weight-semibold);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  
  ${mediaQueries.small} {
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 6px;
  color: ${props => props.theme.colors.darkGray};
  font-weight: var(--font-weight-medium);
`;

const RequiredMark = styled.span`
  color: ${props => props.theme.colors.secondary};
  margin-left: 4px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 32px;
  font-size: 16px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #c42b12;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 14px;
  margin-top: 6px;
`;

const FieldHint = styled.div`
  color: ${props => props.theme.colors.darkGray};
  font-size: 12px;
  margin-top: 4px;
  font-style: italic;
  opacity: 0.8;
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.green};
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &::before {
    content: "✓";
    display: inline-block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: white;
    color: ${props => props.theme.colors.green};
    font-weight: bold;
    line-height: 24px;
    text-align: center;
  }
`;

// Composant principal
const ContactModal: React.FC = () => {
  const { isOpen, pageContext, closeModal } = useContext(ContactContext);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Log pour le débogage
  React.useEffect(() => {
    console.log('ContactModal monté, environnement:', {
      NODE_ENV: process.env.NODE_ENV,
      pageContext: pageContext || 'Aucun contexte'
    });
  }, [pageContext]);
  
  if (!isOpen) return null;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is being edited
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    // Validation du nom (obligatoire)
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est obligatoire';
    }
    
    // Validation que l'email ou téléphone soit fourni
    if (!formData.email.trim() && !formData.mobile.trim()) {
      newErrors.email = 'Email ou téléphone obligatoire';
      newErrors.mobile = 'Email ou téléphone obligatoire';
    }
    
    // Validation du format email si fourni
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format email invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // En mode développement, nous envoyons à notre API locale
      // En production, nous envoyons à l'API déployée
      console.log('Envoi des données du formulaire à l\'API');
      
      // Déterminer l'URL de l'API en fonction de l'environnement
      let apiUrl;
      
      if (process.env.NODE_ENV === 'production') {
        // En production, on utilise un chemin relatif car l'API est exposée au même domaine via Traefik
        apiUrl = '/api/contact';
        
        // Si une URL spécifique est fournie dans les variables d'environnement, l'utiliser
        if (process.env.REACT_APP_API_URL) {
          apiUrl = `${process.env.REACT_APP_API_URL}/contact`;
        }
      } else {
        // En développement, on utilise l'API locale sur le port 3001
        apiUrl = 'http://localhost:3001/api/contact';
      }
      
      console.log('Appelant API à:', apiUrl, 'avec contexte:', pageContext || 'Aucun contexte');
        
      // Appel à l'API backend
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobile: formData.mobile,
          company: formData.company,
          message: formData.message,
          context: pageContext || 'Site web' // Ajout du contexte de la page
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur API:', errorData);
        throw new Error(errorData.error || 'Erreur lors de l\'envoi du message');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur inconnue');
      }
      
      // Si l'API est en mode simulation, on l'indique dans la console
      if (data.mode === 'simulation') {
        console.log('API en mode simulation - pas d\'envoi réel à Slack');
      }
      
      // Réinitialisation du formulaire et affichage du message de succès
      setFormData(initialFormData);
      setSubmitSuccess(true);
      
      // Fermeture automatique après 3 secondes
      setTimeout(() => {
        closeModal();
        setSubmitSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Erreur:', error);
      setErrors({ message: 'Une erreur est survenue, veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };
  
  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <CloseButton onClick={closeModal} aria-label="Fermer">
          ×
        </CloseButton>
        
        <ModalTitle>Contactez-nous</ModalTitle>
        
        {submitSuccess ? (
          <SuccessMessage>
            Votre message a bien été envoyé ! Nous vous répondrons dans les plus brefs délais.
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>Prénom</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Votre prénom"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>
                  Nom<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Votre nom"
                />
                <FieldHint>* Champ obligatoire</FieldHint>
                {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label>
                  Téléphone mobile<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Votre numéro de téléphone"
                />
                <FieldHint>* Email ou téléphone requis</FieldHint>
                {errors.mobile && <ErrorMessage>{errors.mobile}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label>
                  Email professionnel<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                />
                <FieldHint>* Email ou téléphone requis</FieldHint>
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Entreprise</Label>
              <Input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Nom de votre entreprise"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Message</Label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Comment pouvons-nous vous aider ?"
              />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </FormGroup>
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
            </SubmitButton>
          </Form>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ContactModal;